import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, Lock } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { supabase } from '../../lib/supabase';
import { updateTeamNhlLogo } from '../../lib/supabase';
import { TeamLogo } from '../TeamLogo';
import toast from 'react-hot-toast';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';

const NHL_TEAMS: { abbrev: string; name: string }[] = [
  { abbrev: 'ANA', name: 'Anaheim Ducks' },
  { abbrev: 'BOS', name: 'Boston Bruins' },
  { abbrev: 'BUF', name: 'Buffalo Sabres' },
  { abbrev: 'CAR', name: 'Carolina Hurricanes' },
  { abbrev: 'CBJ', name: 'Columbus Blue Jackets' },
  { abbrev: 'CGY', name: 'Calgary Flames' },
  { abbrev: 'CHI', name: 'Chicago Blackhawks' },
  { abbrev: 'COL', name: 'Colorado Avalanche' },
  { abbrev: 'DAL', name: 'Dallas Stars' },
  { abbrev: 'DET', name: 'Detroit Red Wings' },
  { abbrev: 'EDM', name: 'Edmonton Oilers' },
  { abbrev: 'FLA', name: 'Florida Panthers' },
  { abbrev: 'LAK', name: 'Los Angeles Kings' },
  { abbrev: 'MIN', name: 'Minnesota Wild' },
  { abbrev: 'MTL', name: 'Montréal Canadiens' },
  { abbrev: 'NSH', name: 'Nashville Predators' },
  { abbrev: 'NJD', name: 'New Jersey Devils' },
  { abbrev: 'NYI', name: 'New York Islanders' },
  { abbrev: 'NYR', name: 'New York Rangers' },
  { abbrev: 'OTT', name: 'Ottawa Senators' },
  { abbrev: 'PHI', name: 'Philadelphia Flyers' },
  { abbrev: 'PIT', name: 'Pittsburgh Penguins' },
  { abbrev: 'SEA', name: 'Seattle Kraken' },
  { abbrev: 'SJS', name: 'San Jose Sharks' },
  { abbrev: 'STL', name: 'St. Louis Blues' },
  { abbrev: 'TBL', name: 'Tampa Bay Lightning' },
  { abbrev: 'TOR', name: 'Toronto Maple Leafs' },
  { abbrev: 'UTA', name: 'Utah Hockey Club' },
  { abbrev: 'VAN', name: 'Vancouver Canucks' },
  { abbrev: 'VGK', name: 'Vegas Golden Knights' },
  { abbrev: 'WSH', name: 'Washington Capitals' },
  { abbrev: 'WPG', name: 'Winnipeg Jets' },
];

export function TeamProfile() {
  usePageTracking('team-profile');

  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();
  const { isDynasty, isLoading: modeLoading } = useLeagueMode();

  const [selectedAbbrev, setSelectedAbbrev] = useState<string | null>(null);
  const [currentAbbrev, setCurrentAbbrev] = useState<string | null>(null);
  // Map abbrev → username des équipes qui ont déjà ce logo (autres que l'utilisateur courant)
  const [takenLogos, setTakenLogos] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) {
      navigate('/select-league');
      return;
    }

    async function loadCurrentLogo() {
      try {
        const { data, error } = await supabase
          .from('leagues_users')
          .select('nhl_team_abbrev')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', Number(currentUserId))
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading team logo:', error);
        } else if (data) {
          const abbrev = (data as { nhl_team_abbrev?: string | null }).nhl_team_abbrev ?? null;
          setCurrentAbbrev(abbrev);
          setSelectedAbbrev(abbrev);
        }

        // Charger les logos déjà pris par les autres équipes de la ligue
        const { data: teamLogos } = await supabase
          .from('getallteam')
          .select('username, nhl_team_abbrev')
          .eq('league_id', selectedLeague!.id)
          .neq('users_user_id', Number(currentUserId))
          .not('nhl_team_abbrev', 'is', null);

        const logoMap = new Map<string, string>();
        (teamLogos ?? []).forEach((row: { username: string; nhl_team_abbrev: string | null }) => {
          if (row.nhl_team_abbrev) {
            logoMap.set(row.nhl_team_abbrev, row.username);
          }
        });
        setTakenLogos(logoMap);
      } finally {
        setLoading(false);
      }
    }

    loadCurrentLogo();
  }, [selectedLeague, currentUserId, navigate]);

  const handleSave = async () => {
    if (!selectedLeague || !currentUserId) return;

    // En mode keeper, bloquer si le logo est déjà pris par une autre équipe
    if (isDynasty && selectedAbbrev && takenLogos.has(selectedAbbrev)) {
      const owner = takenLogos.get(selectedAbbrev);
      toast.error(
        language === 'fr'
          ? `Ce logo est déjà utilisé par l'équipe de ${owner}`
          : `This logo is already used by ${owner}'s team`
      );
      return;
    }

    setSaving(true);
    const result = await updateTeamNhlLogo(
      selectedLeague.id,
      Number(currentUserId),
      selectedAbbrev
    );
    setSaving(false);

    if (result.success) {
      setCurrentAbbrev(selectedAbbrev);
      toast.success(language === 'fr' ? 'Logo sauvegardé !' : 'Logo saved!');
    } else {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving logo');
    }
  };

  if (!selectedLeague) return null;

  if (loading) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  const hasChanges = selectedAbbrev !== currentAbbrev;

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-3 p-md-5">
        {/* Back button */}
        <button
          type="button"
          className={`btn btn-link p-0 d-flex align-items-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}
          onClick={() => navigate('/my-office')}
        >
          <ArrowLeft size={18} className="me-2" />
          {language === 'fr' ? 'Retour au bureau' : 'Back to office'}
        </button>

        {/* Header */}
        <div className="text-center mb-4">
          <ImageIcon className="text-primary mb-2" size={40} />
          <h2 className="h4 mb-1">
            {language === 'fr' ? "Logo de mon équipe" : "My Team Logo"}
          </h2>
          <p className="text-muted small">
            {language === 'fr'
              ? `Choisissez le logo NHL qui représentera votre équipe dans la ligue "${selectedLeague.name}"`
              : `Choose the NHL logo that will represent your team in the "${selectedLeague.name}" league`}
          </p>
        </div>

        {/* Current logo display */}
        {currentAbbrev && (
          <div className="text-center mb-4">
            <p className="text-muted small mb-2">
              {language === 'fr' ? 'Logo actuel :' : 'Current logo:'}
            </p>
            <div className="d-flex justify-content-center">
              <TeamLogo abbrev={currentAbbrev} size={80} />
            </div>
          </div>
        )}

        {/* Note mode keeper */}
        {isDynasty && !modeLoading && (
          <div className="alert alert-info d-flex align-items-start gap-2 mb-4 py-2" role="alert">
            <Lock size={16} className="mt-1 flex-shrink-0" />
            <small>
              {language === 'fr'
                ? 'Mode keeper : chaque logo ne peut être choisi que par une seule équipe dans cette ligue.'
                : 'Keeper mode: each logo can only be chosen by one team in this league.'}
            </small>
          </div>
        )}

        {/* Team grid */}
        <div className="mb-4">
          <p className="text-muted small mb-3">
            {language === 'fr'
              ? 'Cliquez sur un logo pour le sélectionner. Cliquez à nouveau pour désélectionner.'
              : 'Click a logo to select it. Click again to deselect.'}
          </p>
          <div className="row row-cols-4 row-cols-sm-6 row-cols-md-8 g-2">
            {NHL_TEAMS.map((team) => {
              const isSelected = selectedAbbrev === team.abbrev;
              // En mode keeper, désactiver les logos déjà pris par une autre équipe
              const isTaken = isDynasty && !modeLoading && takenLogos.has(team.abbrev);
              const takenByUser = isTaken ? takenLogos.get(team.abbrev) : undefined;
              const tooltipTitle = isTaken
                ? (language === 'fr' ? `Déjà choisi par ${takenByUser}` : `Already chosen by ${takenByUser}`)
                : team.name;
              return (
                <div key={team.abbrev} className="col">
                  <button
                    type="button"
                    className="btn w-100 p-1 rounded-3 d-flex flex-column align-items-center position-relative"
                    style={{
                      border: isSelected
                        ? '2px solid var(--bs-primary)'
                        : '2px solid transparent',
                      background: isSelected
                        ? 'rgba(var(--bs-primary-rgb), 0.08)'
                        : 'transparent',
                      opacity: isTaken ? 0.35 : 1,
                      cursor: isTaken ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => {
                      if (isTaken) return;
                      setSelectedAbbrev(isSelected ? null : team.abbrev);
                    }}
                    title={tooltipTitle}
                    disabled={isTaken}
                  >
                    <TeamLogo abbrev={team.abbrev} size={48} />
                    {isTaken && (
                      <Lock
                        size={12}
                        className="position-absolute text-secondary"
                        style={{ top: 4, right: 4 }}
                      />
                    )}
                    <span
                      className={`mt-1 fw-semibold ${isSelected ? 'text-primary' : 'text-muted'}`}
                      style={{ fontSize: '0.6rem' }}
                    >
                      {team.abbrev}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="d-flex gap-3 justify-content-center flex-wrap">
          {selectedAbbrev && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setSelectedAbbrev(null)}
              disabled={saving}
            >
              {language === 'fr' ? 'Aucun logo' : 'No logo'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary px-4"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving
              ? language === 'fr'
                ? 'Sauvegarde...'
                : 'Saving...'
              : language === 'fr'
              ? 'Sauvegarder'
              : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
